import { useEffect, useState } from "react";
import ChapterCard from "../chapter-overview-dashboard/components/ChapterCard";
import ProgressSummary from "../chapter-overview-dashboard/components/ProgressSummary";
import { useAuth } from "context/AuthContext";
import ActionItemsPanel from "pages/chapter-overview-dashboard/components/ActionItemsPanel";
import { CourseAPI } from "service";
import Header from "components/ui/Header";
import Icon from "components/AppIcon";
import { useNavigate } from "react-router-dom";

const SelectQuizCourse = () => {
  const [dashboardData, setDashboardData] = useState([]);

  const navigate = useNavigate();


  const { courseOverview } = useAuth()

  const staticCourses = [
    {
      id: "1",
      title: "Introduction to AI",
      description: "Explore the fundamentals of artificial intelligence, its history, and core concepts that form the foundation of modern AI systems.",
      status: "current",
      progress: 0,
      lessons: 5,
      duration: "45 min",
      quizScore: 0,
      estimatedTime: "45 minutes",
      timeSpentInMinutes: 0,
      numberOfSlides: 13,
      currentSlide: 1,
    },
    {
      id: "2",
      title: "Machine Learning Basics",
      description: "Dive into machine learning algorithms, supervised and unsupervised learning, and understand how machines learn from data.",
      status: "current",
      progress: 0,
      lessons: 7,
      duration: "60 min",
      quizScore: 0,
      estimatedTime: "60 minutes",
      numberOfSlides: 20,
      currentSlide: 1,
      timeSpentInMinutes: 0,

    },
    {
      id: "3",
      title: "AI Ethics & Future",
      description: "Examine the ethical implications of AI, bias in algorithms, and explore the future possibilities and challenges of artificial intelligence.",
      status: "current",
      progress: 0,
      lessons: 6,
      duration: "50 min",
      quizScore: 0,
      estimatedTime: "50 minutes",
      numberOfSlides: 13,
      currentSlide: 1,
      timeSpentInMinutes: 0,
    },
    {
      id: "4",
      title: "AI Ethics & Future",
      description: "Examine the ethical implications of AI, bias in algorithms, and explore the future possibilities and challenges of artificial intelligence.",
      status: "current",
      progress: 0,
      lessons: 6,
      duration: "50 min",
      quizScore: 0,
      estimatedTime: "50 minutes",
      numberOfSlides: 13,
      currentSlide: 1,
      timeSpentInMinutes: 0,
    }
  ];

  const actionItems = [
    {
      id: 1,
      type: "incomplete",
      priority: "high",
      title: "Complete Advanced Techniques Presentation",
      description: "Finish the remaining presentation in Chapter 3 to unlock the final quiz",
      chapter: "Chapter 3",
      estimatedTime: "24 min",
      actionText: "Continue"
    },
    {
      id: 2,
      type: "failed",
      priority: "medium",
      title: "Retake Prompting Basics Quiz",
      description: "Your score of 76% is below the 80% mastery threshold",
      chapter: "Chapter 3",
      estimatedTime: "15 min",
      attempts: 1,
      actionText: "Retake"
    },
    {
      id: 3,
      type: "review",
      priority: "low",
      title: "Review Machine Learning Concepts",
      description: "Strengthen understanding before moving to advanced topics",
      chapter: "Chapter 2",
      estimatedTime: "30 min",
      actionText: "Review"
    }
  ];

  const completedChapters = dashboardData.filter(chapter => chapter.status === 'completed').length;
  const overallProgress = Math.round((completedChapters / dashboardData.length) * 100);

  useEffect(() => {
    if (!courseOverview?.coursesProgress) return;

    const progressMap = Object.fromEntries(
      courseOverview.coursesProgress.map(progress => [progress.courseRef, progress])
    );

    const mergedData = staticCourses.map(course => {
      const progress = progressMap[course.id];


      if (progress?.progress > 0 && progress?.progress < 100) {
        course.status = 'current';
      } else if (progress?.progress === 100) {
        course.status = 'completed';
      } else {
        course.status = 'not-started';
      }

      return {
        ...course,
        ...(progress && {
          progress: progress.progress ?? course.progress,
          quizScore: progress.bestScore ?? course.quizScore,
          timeSpentInMinutes: progress.timeSpentInMinutes ?? course.timeSpentInMinutes,
          numberOfSlides: progress.numberOfSlides ?? course.numberOfSlides,
          currentSlide: progress.currentSlide ?? course.currentSlide,
        })
      };
    });

    setDashboardData(mergedData);
  }, [courseOverview]);


  const handleChapterSelect = async (chapter) => {
    try {
      await CourseAPI.updateProgress(chapter.id, chapter.progress, chapter.timeSpentInMinutes, chapter.numberOfSlides, chapter.currentSlide, chapter.title);
    } catch (error) {
      console.error("Error fetching progress:", error);
    }

    localStorage.setItem('currentChapter', JSON.stringify(chapter));

    if (chapter.status === 'completed') {
      navigate(`${'/interactive-presentation-viewer'}?courseId=${chapter.id}`, {
        state: { chapterId: chapter.id, mode: 'review', name: chapter.title }
      });
    } else if (chapter.status === 'current' || chapter.status === 'not-started') {
      navigate(`${'/interactive-presentation-viewer'}?courseId=${chapter.id}`, {
        state: { chapterId: chapter.id, mode: 'continue', name: chapter.title }
      });
    }
  };

  const handleQuizSelect = (chapter) => {
    localStorage.setItem('currentChapter', JSON.stringify(chapter));
    navigate(`/quiz-assessment?chapterId=${chapter.id}`, {
      state: { chapterId: chapter.id, mode: 'continue', name: chapter.title }
    });
  };

  return (
    <div className="container min-h-screen py-24 mx-auto sm:px-6 lg:px-8 max-w-7xl bg-background">
      <Header />

      <div className="mb-8">
        <div className="flex items-center mb-4 space-x-3">
          <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl">
            <Icon name="TrendingUp" size={24} color="white" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-3xl font-bold font-heading text-text-primary">
              Quiz
            </h1>
            <p className="text-text-secondary">
              Take quizzes to assess your understanding of AI concepts and track your progress.
            </p>
          </div>
        </div>
      </div>
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-12 ">
        {/* Chapters Section */}
        <div className="space-y-6 lg:col-span-2">
          <div>
            <h2 className="mb-2 text-2xl font-bold font-heading text-text-primary">
              Available Quiz Chapters
            </h2>
            <p className="text-text-secondary">
              Select a chapter to start or continue your quiz assessment.
            </p>
          </div>

          <div className="space-y-4">
            {dashboardData.map((chapter) => (
              <ChapterCard key={chapter.id} chapter={chapter} onChapterSelect={handleChapterSelect} quiz handleQuizSelect={handleQuizSelect} />
            ))}
          </div>
        </div>
      </div>

      {/* Mobile-Optimized Chapter Grid */}
      <div className="lg:hidden">
        <div className="grid grid-cols-1 gap-4">
          {dashboardData.map((chapter) => (
            <ChapterCard key={`mobile-${chapter.id}`} chapter={chapter} onChapterSelect={handleChapterSelect} quiz handleQuizSelect={handleQuizSelect} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SelectQuizCourse;
